import { z } from 'zod';

import type { AgentForm as AgentFormType } from '../../../utilities';

type NumericValue = number | string | null | undefined;

const nonEmptyStringSchema = z.string().min(1, 'Must be a non-empty string');

const toNumber = (value: NumericValue): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const makeRangeValidator =
  (min: number, max: number, unit: string) =>
  (value: NumericValue): string | undefined => {
    const n = toNumber(value);
    if (n === undefined) return undefined;

    const result = z
      .number({ invalid_type_error: 'Must be a number' })
      .min(min, `Must be between ${min} and ${max} ${unit}`)
      .max(max, `Must be between ${min} and ${max} ${unit}`)
      .safeParse(n);
    if (!result.success) return result.error.issues[0]?.message;
    return undefined;
  };

const makeIntRangeValidator =
  (min: number, max: number, unit: string) =>
  (value: NumericValue): string | undefined => {
    const n = toNumber(value);
    if (n === undefined) return undefined;

    if (!Number.isInteger(n)) return 'Must be a whole number';

    const result = z
      .number({ invalid_type_error: 'Must be a number' })
      .int('Must be a whole number')
      .min(min, `Must be between ${min} and ${max} ${unit}`)
      .max(max, `Must be between ${min} and ${max} ${unit}`)
      .safeParse(n);
    if (!result.success) return result.error.issues[0]?.message;
    return undefined;
  };

export const validateEndpointingDelays = (
  _: unknown,
  form: AgentFormType
): string | undefined => {
  const rawMin = form.turn_detector_endpointing_min_delay;
  const rawMax = form.turn_detector_endpointing_max_delay;

  const minN = toNumber(rawMin);
  const maxN = toNumber(rawMax);

  const delaySchema = z
    .number({ invalid_type_error: 'Must be a number' })
    .min(0, 'Delay must be 0 or greater');

  if (minN !== undefined) {
    const minResult = delaySchema.safeParse(minN);
    if (!minResult.success) return minResult.error.issues[0]?.message;
  }

  if (maxN !== undefined) {
    const maxResult = delaySchema.safeParse(maxN);
    if (!maxResult.success) return maxResult.error.issues[0]?.message;
  }

  if (minN != null && maxN != null && maxN < minN) {
    return 'Max delay must be greater than or equal to min delay';
  }

  return undefined;
};

export const validateRealtimeThreshold = (
  value: NumericValue
): string | undefined => {
  const n = toNumber(value);
  if (n === undefined) return undefined;

  const result = z
    .number({ invalid_type_error: 'Must be a number' })
    .min(0, 'Value must be between 0.0 and 1.0')
    .max(1, 'Value must be between 0.0 and 1.0')
    .safeParse(n);
  if (!result.success) return result.error.issues[0]?.message;
  return undefined;
};

export const validateModelPath = (
  value: string | null | undefined
): string | undefined => {
  if (value == null || value === '') return undefined;

  const result = nonEmptyStringSchema.safeParse(value.trim());
  if (!result.success) return result.error.issues[0]?.message;
  return undefined;
};

export const validateVadMinSpeechDuration = makeRangeValidator(
  0.01,
  1.0,
  'sec'
);
export const validateVadMinSilenceDuration = makeRangeValidator(
  0.1,
  3.0,
  'sec'
);
export const validateVadPrefixPaddingDuration = makeRangeValidator(
  0.0,
  2.0,
  'sec'
);
export const validateVadMaxBufferedSpeech = makeRangeValidator(
  10.0,
  120.0,
  'sec'
);

export const validateInterruptionTimeout = makeRangeValidator(0.0, 5.0, 'sec');
export const validateRealtimePrefixPaddingMs = makeIntRangeValidator(
  0,
  2000,
  'ms'
);
export const validateRealtimeSilenceDurationMs = makeIntRangeValidator(
  0,
  5000,
  'ms'
);
